import {
  adjustments,
  layoutStates,
  openTextBrands,
  pageStates,
  paymentTypes,
  purchaseErrors,
  purchaseErrorsNotifications,
  terms,
} from "@/components/Buy/BuyEnums";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { useUserStore } from "@/stores/user";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { formatDateString } from "@/common/dateFormat";
import {
  DEFAULT_QUANTITY,
  DEFAULT_SKU,
  DEFAULT_TERM_LENGTH_MONTHS,
  EMPTY_GUID,
  SAFE_RENEWAL_WINDOW_DAYS,
  URLS,
  buyFlowRouteName,
  carbonitePayPalGateway,
  webrootPayPalGateway,
} from "@/define";
import { NotificationLookup, NotificationType, useNotificationsStore } from "@/stores/notifications";
import { LocationQuery } from "vue-router";
import { useUnauthenticatedBuyFlowStore } from "@/stores/unauthorizedBuyflow";
import { unifiedApi } from "@/common";
import {
  ICartItem,
  IPaymentMethod,
  IPayPalApprovalResponse,
  IPayPalShippingAddressInfo,
  IRatePlan,
  IShoppingCart,
} from "@/common/api/unifiedPortal/interfaces";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { IBeginShoppingCartRequest, ISetupCartRequest, ISubscriptionResult } from "@/components/Buy/Interfaces";
import { usePaymentPageStore } from "@/stores/paymentPage";
import { pageStates as paymentPageStates } from "@/components/Buy/Payment/PaymentConfigs";
import { getDeviceInfo, getUserDetailsFromStore } from "@/common/userDetails";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import { logEvent } from "@/common/logger";
import router from "@/routes";
import { cartUpdate, googleTagCartEvents } from "@/common/googleTagEvents";
import { useSiteSettingsStore } from "@/stores/siteSettings";
import { useSecurityStore } from "@/stores/security";
import { getRedirectToLoginURL, getRedirectToLoginURLWithEmail } from "@/common/loginRedirect";
import {
  isFirstNameValid,
  isLastNameValid,
  isAddress1Valid,
  isZipValid,
  isStateValid,
  isCityValid,
} from "./Payment/ShippingFormValidation";
import { generateInterstitialReturnPortalDestination } from "@/common/subscriptionHelper";
import { getUrl } from "@/common/getUrl";
import { usePayPalStore } from "@/stores/payPal";
import { groupedProducts } from "@/common/GroupedProducts";
import { bestMatchingCandyRackRatePlan, getSpecialOffersToDisplay } from "@/components/Buy/SpecialOffersHelper";
import { addBuyFlowLogEntry } from "@/common/buyflowTracker";

export const adjustmentsOrder = [
  adjustments.BonusMonthsPromo,
  adjustments.PromoCodeAmt,
  adjustments.PriorSubscriptionCredit,
  adjustments.TrialDaysRemaining,
  adjustments.LicenseDaysRemaining,
  adjustments.Tax,
];

export const payPalGatewayBrandMap = {
  [openTextBrands.Carbonite]: [carbonitePayPalGateway],
  [openTextBrands.Webroot]: [webrootPayPalGateway],
};

export function removeStars(v: string | undefined) {
  return v ? v.replaceAll("*", "") : "";
}

//Only formats in USD for now. May need to update to other currencies in the future
export function formatMoney(amount = 0) {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function getTermSuffix(months: number | undefined) {
  if (!months) {
    return "";
  }

  let suffix = terms.monthToMonth;
  if (months === 12) {
    suffix = terms.year;
  }
  if (months > 12) {
    suffix = terms.years;
  }

  return `Common.${suffix}`;
}

export function setupCartForGroupedProducts(sku: string) {
  //Get out if its not a grouped product sku
  if (sku && !groupedProducts[sku]) {
    return;
  }
  const buyFlowStore = useBuyFlowStore();
  const groupedProduct = groupedProducts[sku];
  let count = 1;

  for (const item of groupedProduct) {
    const ratePlanId = buyFlowStore.getRatePlanIdBySku(
      item.sku,
      item.months,
      item?.includedUnits || item?.includedIdentities
    );

    if (count === 1) {
      const beginShoppingCartRequest = {
        sku: item.sku,
        ratePlanId: ratePlanId,
        quantity: 1,
        autoRenew: true,
      };

      buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
    } else {
      const cartItem = {
        sku: item.sku,
        ratePlanId: ratePlanId,
        quantity: 1,
        autoRenew: true,
      } as ICartItem;
      buyFlowStore.cart?.items.push(cartItem);
    }

    count += 1;
  }
}

export async function setupCartNewUser(request: ISetupCartRequest) {
  const buyFlowStore = useBuyFlowStore();
  let beginShoppingCartRequest: IBeginShoppingCartRequest;

  //If the sku is in the config for grouped products add all products to the cart
  if (request.sku && groupedProducts[request.sku]) {
    setupCartForGroupedProducts(request.sku);
  } else if (request.ratePlanId) {
    const sku = buyFlowStore.getSkuByRatePlan(request.ratePlanId);
    beginShoppingCartRequest = {
      sku: sku,
      ratePlanId: request.ratePlanId,
      quantity: request.quantity,
      deviceId: request.deviceId,
    };
    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
    buyFlowStore.setRecommendSkuFromCurrent(sku);
  } else {
    if (request.isWebroot && !request.sku && !request.ratePlanId && !request.licenseKey) {
      //We have nothing to create a cart for WR so we should show an empty cart
      changePageState(pageStates.email);
      return;
    }

    beginShoppingCartRequest = {
      sku: request.sku || DEFAULT_SKU,
      ratePlanId: buyFlowStore.getRatePlanIdBySku(
        request.sku || DEFAULT_SKU,
        request.subscriptionMonths || DEFAULT_TERM_LENGTH_MONTHS,
        request.includedUnits
      ),
      quantity: request.quantity,
      deviceId: request.deviceId,
    };
    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
    buyFlowStore.setRecommendSkuFromCurrent(request.sku || DEFAULT_SKU);
  }

  //We shouldn't expect all user who just go to /buy to be brand new users.
  changePageState(request.step ? pageStates[request.step] : pageStates.email);
}

export async function setupCartForUser(request: ISetupCartRequest) {
  const buyFlowStore = useBuyFlowStore();
  const user = useUserStore();
  let beginShoppingCartRequest: IBeginShoppingCartRequest;

  //RatePlanId is the highest value query parameter. If we are passed one it should be used above everything else
  if (request.ratePlanId) {
    const sku = buyFlowStore.getSkuByRatePlan(request.ratePlanId);
    beginShoppingCartRequest = { sku: sku, ratePlanId: request.ratePlanId, quantity: request.quantity };

    if (request.deviceId) {
      beginShoppingCartRequest.deviceId = request.deviceId;
    }

    if (request.licenseKey) {
      beginShoppingCartRequest.licenseKey = request.licenseKey;
    }

    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);

    const ratePlan = buyFlowStore.getRatePlanInfo(request.ratePlanId);
    let step = getBuyFlowStepForRequest(request);
    //Flat rate upgrades should start on the payment step
    if (ratePlan?.isFlatRateUpgrade) {
      step = pageStates.payment;
    }
    changePageState(step);
  } else if (request.deviceId) {
    //Look up the sub based on the device Id
    const subscription = await getSubscriptionFromDeviceId(request.deviceId);

    if (subscription) {
      if (request.sku) {
        beginShoppingCartRequest = {
          deviceId: request.deviceId,
          subscriptionNumber: subscription.subscriptionNumber || request.subscriptionNumber,
          sku: request.sku,
          ratePlanId: buyFlowStore.getRatePlanIdBySku(
            request.sku,
            request.subscriptionMonths || DEFAULT_TERM_LENGTH_MONTHS
          ),
          quantity: request.quantity,
        };
      } else {
        //Use values from given device
        beginShoppingCartRequest = {
          deviceId: request.deviceId,
          subscriptionNumber: subscription.subscriptionNumber || request.subscriptionNumber,
          sku: subscription.sku,
          ratePlanId: subscription.ratePlanId,
          quantity: request.quantity,
        };
      }

      //Get the device name from the computer on the subscription
      buyFlowStore.computerName = subscription.computerName;
      buyFlowStore.setRecommendSkuFromCurrent(subscription.sku);
      buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
      changePageState(getBuyFlowStepForRequest(request));
    } else {
      //If we don't find a subscription for the deviceId,
      //try to setup a new cart with what we have from the query parameters or default items
      setupCartNewUser(request);
    }
  } else if (request.sku && !request.isSeatCountUpgrade) {
    const defaultSkuCatalogItem = buyFlowStore.getCatalogItemBySku(DEFAULT_SKU);
    const catalogItem = buyFlowStore.getCatalogItemBySku(request.sku);

    //Check the upgrade order to set the recommended sku
    if (
      !request.isWebroot &&
      defaultSkuCatalogItem &&
      catalogItem &&
      defaultSkuCatalogItem?.upgradeOrder &&
      catalogItem?.upgradeOrder &&
      defaultSkuCatalogItem?.upgradeOrder > catalogItem?.upgradeOrder
    ) {
      buyFlowStore.recommendedPlanSku = DEFAULT_SKU;
    } else {
      buyFlowStore.recommendedPlanSku = request.sku;
    }

    const ratePlanId = buyFlowStore.getRatePlanIdBySku(
      request.sku,
      request.subscriptionMonths || DEFAULT_TERM_LENGTH_MONTHS,
      request.includedUnits
    );

    beginShoppingCartRequest = {
      sku: request.sku,
      ratePlanId: ratePlanId,
      quantity: request.quantity,
      deviceId: request.deviceId,
      subscriptionNumber: request.subscriptionNumber,
      licenseKey: request.licenseKey,
    };
    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);

    changePageState(getBuyFlowStepForRequest(request));
  } else if (request.licenseKey && buyFlowStore.licenseKeyInfo) {
    beginShoppingCartRequest = {
      sku: buyFlowStore.licenseKeyInfo.sku,
      ratePlanId:
        request.isSeatCountUpgrade && buyFlowStore.licenseKeyInfo.seatUpgradeRatePlanId
          ? buyFlowStore.licenseKeyInfo.seatUpgradeRatePlanId
          : buyFlowStore.licenseKeyInfo.currentRatePlanId,
      licenseKey: request.licenseKey,
    };

    buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
    changePageState(getBuyFlowStepForRequest(request));
  } else if (request.licenseKey && !buyFlowStore.licenseKeyInfo && !request.sku && !request.ratePlanId) {
    //We don't have enough to build a cart. This could be because the purchase info call failed and we don't have the license key info
    //Without a rate plan id or sku we have no way to know what to put in the cart, so we should just show an empty cart
    return;
  } else {
    //Get the default sku and rate plan from the plans.
    //Note:This can be removed when we move a multi cart and allow for an empty cart
    buyFlowStore.recommendedPlanSku = "";
    setDefaultSkuAndTerm(request);
    changePageState(request.step ? pageStates[request.step] : pageStates.email);
  }
  //Once the cart is built add the userGuid
  if (buyFlowStore.cart) {
    buyFlowStore.cart.userGuid = user.currentUser ? user.currentUser.userGuid : request.userGuid || "";
  }
}

export function getBuyFlowStepForRequest(request: ISetupCartRequest) {
  const buyFlowStore = useBuyFlowStore();
  const userStore = useUserStore();
  const isLoggedIn = userStore.currentUser;
  let step = request.step ? pageStates[request.step] : pageStates.email;

  if (
    isLoggedIn &&
    buyFlowStore.cart &&
    (buyFlowStore.currentBrand === openTextBrands.Webroot || buyFlowStore.openTextBrand === openTextBrands.Webroot) &&
    getSpecialOffersToDisplay(buyFlowStore.cart.items[0]).length > 0
  ) {
    step = pageStates.email;
  }
  //If this is a Webroot cart and there is no license key info, but we have a userGUID set the page to the email step.
  //Since we only have the userGUID we have no way of telling if the user already owns something in the candy rack either.
  else if (
    buyFlowStore.userGuid &&
    !buyFlowStore.licenseKeyInfo &&
    (buyFlowStore.currentBrand === openTextBrands.Webroot || buyFlowStore.openTextBrand === openTextBrands.Webroot)
  ) {
    step = pageStates.email;
  } else if (isLoggedIn && buyFlowStore.cart && getSpecialOffersToDisplay(buyFlowStore.cart.items[0]).length === 0) {
    //If there are no candy rack items to show and the user has a safe account move to payment
    step = pageStates.payment;
  } else if (request.licenseKey && !isLoggedIn) {
    //If there is a license key and the user is not logged in we need to show the email step to verify the user owns the license key before moving forward
    step = pageStates.email;
  }
  return step;
}

export function setDefaultSkuAndTerm(request?: ISetupCartRequest) {
  const buyFlowStore = useBuyFlowStore();
  const defaultProduct = buyFlowStore.getBasePlan();
  let ratePlanId = defaultProduct.ratePlanId;

  if (request && request.subscriptionMonths) {
    ratePlanId = buyFlowStore.getRatePlanIdBySku(defaultProduct.sku, request.subscriptionMonths);
  }

  buyFlowStore.setRecommendSkuFromCurrent();
  const beginShoppingCartRequest = {
    sku: defaultProduct.sku,
    ratePlanId: ratePlanId,
    quantity: request?.quantity || DEFAULT_QUANTITY,
  };
  buyFlowStore.beginShoppingCart(beginShoppingCartRequest);
}

export function getSubscriptionFromDeviceId(deviceId: number): ISubscriptionResult {
  const userStore = useUserStore();
  if (!userStore.currentUser) {
    const unauthenticatedBuyflowStore = useUnauthenticatedBuyFlowStore();
    return {
      computerName: "",
      sku: unauthenticatedBuyflowStore.subscription?.sku || "",
      subscriptionNumber: "",
      ratePlanId: unauthenticatedBuyflowStore.subscription?.productRatePlanId || "",
    } as ISubscriptionResult;
  }

  const subscriptionStore = useSubscriptionsStore();
  const sub = subscriptionStore.subscriptions.find(l => l.computer?.id === deviceId);

  return {
    computerName: sub?.computer?.description || sub?.computer?.name || "",
    sku: sub?.sku || "",
    subscriptionNumber: sub?.zuoraSubscriptionNumber || "",
    ratePlanId: sub?.ratePlanId || "",
  } as ISubscriptionResult;
}

export function getComputerName(computerId): string {
  const buyFlowStore = useBuyFlowStore();
  let name = "";

  const di = getDeviceInfo(parseInt(computerId));
  if (di.device) {
    name = di.device.description || di.device.name;
  } else if (buyFlowStore.getCartItemByComputerId(computerId)) {
    const item = buyFlowStore.getCartItemByComputerId(computerId);
    name = item?.computerDescription || item?.computerName || "";
  }
  return name;
}

//Gets the first item from the cartItems for the expirations date
//Will need to update UX/Code for multi cart items
export function getRenewDate() {
  const buyFlowStore = useBuyFlowStore();
  if (buyFlowStore.cart && buyFlowStore.cart.items.length > 0) {
    // The expirationDate returned by Hoth is the ServiceEndDate which represents the last day of the subscription
    const renewDate = new Date(buyFlowStore.cart?.items[0]?.expirationDate ?? "");
    // So we need to add 1 day to display the proper renew date
    renewDate.setDate(renewDate.getDate() + 1);
    return formatDateString(renewDate.toString() || "");
  }
}

// if we are an annual consumer selecting a monthly plan outside of the conversion period (last 30 days of annual plan or the 30 days after that)
export function isInMonthlyConversionWindow(subscriptionNumber: string) {
  let changeable = true;
  const subscriptionStore = useSubscriptionsStore();
  const subscription = subscriptionStore.subscriptions.find(l => l.zuoraSubscriptionNumber === subscriptionNumber);
  if (subscription) {
    changeable = subscription.remainingDays <= SAFE_RENEWAL_WINDOW_DAYS;
  }
  return changeable;
}

export function changePageState(page: pageStates, clearNotifications = true) {
  const notificationsStore = useNotificationsStore();
  const buyFlowStore = useBuyFlowStore();
  //Always switch page to the purchase layout
  buyFlowStore.layoutState = layoutStates.purchase;
  if (clearNotifications) {
    notificationsStore.clearNotifications();
  }
  buyFlowStore.pageState = page;
  buyFlowStore.triggerStateChange = !buyFlowStore.triggerStateChange;
  const e = document.getElementById("vueApp");
  e?.scrollIntoView();
}

export function buildQueryString(routeQueryParams: LocationQuery, skip: string[] = []) {
  let queryParams = "";
  for (const key in routeQueryParams) {
    //skip keys found in the skip array
    if (skip.find(l => l === key)) {
      continue;
    }

    if (!queryParams) {
      queryParams = `${key}=${routeQueryParams[key]}`;
    } else {
      queryParams = `${queryParams}&${key}=${routeQueryParams[key]}`;
    }
  }

  return queryParams;
}

export async function loadPaymentMethods(forcePaymentsCall = false, bubbleUpError = false, isFromBuyFlow = false) {
  try {
    const buyFlowStore = useBuyFlowStore();
    const userStore = useUserStore();
    let paymentMethods: IPaymentMethod[] = [];

    if (userStore.isEstablishedAccount || forcePaymentsCall) {
      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call start: get User Payment Methods");
      }
      const response = (await unifiedApi.getUserPaymentMethods()).data;
      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call end: get User Payment Methods");
      }
      if (response["paymentMethods"]) {
        paymentMethods = response["paymentMethods"];
      }
    } else {
      //If there is no payment method id return empty array to avoid error page
      if (!buyFlowStore.cart?.paymentMethodId) {
        return [];
      }
      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call start: get User Payment Method Guest");
      }
      //All credit cards added during an unauthenticated or preliminary flow are orphaned by default. We need to pass an empty GUID to get those cards back
      const response = (
        await unifiedApi.getUserPaymentMethodGuest(EMPTY_GUID, buyFlowStore.cart?.paymentMethodId ?? "")
      ).data;

      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call start: get User Payment Method Guest");
      }

      if (!response.id) {
        return paymentMethods;
      }

      const paymentMethod = {
        address: response.address,
        email: response.email,
        expirationDate: response.expirationDate, //Create a date string for the first of the given month
        lastDigits: response.lastDigits,
        cardType: response.cardType,
        id: response.id,
        accountHolder: response.accountHolder,
        defaultPaymentMethod: response.defaultPaymentMethod,
      } as IPaymentMethod;

      paymentMethods[0] = paymentMethod;
    }

    return paymentMethods;
  } catch (err) {
    if (bubbleUpError) {
      throw err;
    } else {
      handleApiError(err as AxiosError);
    }
  }
}

export function handlePurchaseError(
  error: string,
  statusCode: number | undefined = undefined,
  isFromPayPalReturn = false,
  payPalReturnRouteName = buyFlowRouteName
) {
  const notificationsStore = useNotificationsStore();
  const paymentPageStore = usePaymentPageStore();
  let notificationError = error;

  const errorObject = NotificationLookup[error];

  //If there is no match to the error check if it matches the purchase errors
  if (!errorObject) {
    const notification =
      error === purchaseErrors.CCAddress
        ? purchaseErrorsNotifications.CCAddress
        : error === purchaseErrors.CCTransaction
          ? purchaseErrorsNotifications.CCTransaction
          : purchaseErrors.PurchaseFailureError; // This error will only show up if statusCode is not 400

    if (statusCode === 400 && notification === purchaseErrors.PurchaseFailureError) {
      notificationsStore.addNotification({
        type: purchaseErrorsNotifications.PurchaseFailureError,
        dismissible: false,
      });
      return;
    }

    notificationError = notification;
  }

  //Go back to payment step and display CC form
  paymentPageStore.pageState = paymentPageStates.forceAddCard;

  if (isFromPayPalReturn) {
    //Remove the item that triggers the auto renew success banner
    sessionStorage.removeItem("product_name_for_auto_renew");
    const payPalStore = usePayPalStore();
    payPalStore.purchaseError = notificationError as purchaseErrorsNotifications;
    const params = { from: "Paypal" };

    if (payPalReturnRouteName === buyFlowRouteName) {
      params["step"] = pageStates.payment;
    }

    router.push({
      name: payPalReturnRouteName,
      params: params,
    });
    return;
  }

  //use passed in error
  notificationsStore.addNotification({ type: notificationError as NotificationType, dismissible: false });

  changePageState(pageStates.payment, false);

  //PurchaseFailureError should stay on the review step
  if (notificationError === purchaseErrors.PurchaseFailureError) {
    return;
  }
}

// Sends an empty cart to backend to clear the cart in DB
// Should not clear the store
export async function clearShoppingCartFromDb() {
  const buyFlowStore = useBuyFlowStore();
  const emptyCart: IShoppingCart = {
    items: [],
    userGuid: "",
    promotionCode: "",
    promoDetail: [],
    campaignId: 0,
    prospectId: 0,
    paymentMethodId: "",
    adjustments: [],
    total: 0,
    zuoraAccountId: "",
    brand: buyFlowStore.openTextBrand,
    utm_Content: null,
    utm_Medium: null,
    utm_Term: null,
    utm_Source: null,
    utm_Campaign: null,
    ac: null,
    rc: null,
    emcid: null,
    isSeatUpgrade: false,
    hidePromoCode: false,
  };
  await unifiedApi.saveShoppingCart(emptyCart);
  cartUpdate(emptyCart, googleTagCartEvents.remove);
}

export function setEmailCartParameters(
  ac: number | null,
  rc: number | null,
  emcid: number | null,
  utm_Content: string | null,
  utm_Medium: string | null,
  utm_Source: string | null,
  utm_Term: string | null,
  utm_Campaign: string | null
) {
  const buyFlowStore = useBuyFlowStore();

  if (buyFlowStore.cart) {
    buyFlowStore.cart.ac = ac;
    buyFlowStore.cart.rc = rc;
    buyFlowStore.cart.emcid = emcid;
    buyFlowStore.cart.utm_Content = utm_Content;
    buyFlowStore.cart.utm_Medium = utm_Medium;
    buyFlowStore.cart.utm_Source = utm_Source;
    buyFlowStore.cart.utm_Term = utm_Term;
    buyFlowStore.cart.utm_Campaign = utm_Campaign;
  }
}

// Check if user is authenticated and reload user store if empty
// This will prevent a authenticated user get on a unauthenticated state if page refreshed
export async function checkLoggedStatus() {
  const userStore = useUserStore();
  try {
    const currentUser = (await unifiedApi.getUser(true))?.data;
    if (currentUser && !userStore.currentUser) {
      await reloadSubscriberInfo();
    }
  } catch {
    logEvent("User not logged in", "buyHelper");
  }
}

export async function getProspectId(prospectGuid: string): Promise<number | undefined> {
  try {
    if (prospectGuid && prospectGuid != "") {
      return (await unifiedApi.getProspectId(prospectGuid)).data;
    }
  } catch (err) {
    handleApiError(err as AxiosError);
  }
}

export function redirectToUnifiedPortalSmbBuyflow() {
  window.location.href = `${URLS.CARBONITE_BUY_URL}/selectplan`;
}

export function redirectToOutstandingInvoice(redirectToLogIn = false, isFromBuyFlow = false) {
  if (isFromBuyFlow) {
    addBuyFlowLogEntry("Redirect to outstanding invoice page");
  }

  if (redirectToLogIn) {
    let appUrl = getUrl("VITE_APP_URL");
    const hostName = window.location.hostname;
    if (hostName.toLowerCase().indexOf(openTextBrands.Webroot.toLowerCase()) > -1) {
      appUrl = getUrl("VITE_APP_WEBROOT_URL");
    }
    window.location.href = `${appUrl}subscriptions`;
  } else {
    router.push({
      name: "subscription_information",
    });
  }
}

export function checkShippingFormForErrors() {
  const buyFlowStore = useBuyFlowStore();
  const skipValidation = ["address2"];

  for (const key in buyFlowStore.shippingAddress) {
    //skip form items that are not required
    if (skipValidation.indexOf(key) >= 0) {
      continue;
    }

    //check for valid format
    validateShippingFormFields(key);
  }

  const shippingFormHasErrors = Object.values(buyFlowStore.showShippingFormErrors).some(l => l.length > 0);
  return shippingFormHasErrors;
}

export function getBrandFromCart() {
  const buyFlowStore = useBuyFlowStore();
  return buyFlowStore.isWebrootSku(buyFlowStore?.cart?.items[0].sku || "")
    ? openTextBrands.Webroot
    : openTextBrands.Carbonite;
}

export function validateShippingFormFields(key) {
  const buyFlowStore = useBuyFlowStore();

  switch (key) {
    case "firstName":
      buyFlowStore.showShippingFormErrors[key] = isFirstNameValid(buyFlowStore.shippingAddress[key]);
      break;
    case "lastName":
      buyFlowStore.showShippingFormErrors[key] = isLastNameValid(buyFlowStore.shippingAddress[key]);
      break;
    case "state":
      buyFlowStore.showShippingFormErrors[key] = isStateValid(buyFlowStore.shippingAddress[key]);
      break;
    case "address1":
      buyFlowStore.showShippingFormErrors[key] = isAddress1Valid(buyFlowStore.shippingAddress[key]);
      break;
    case "city":
      buyFlowStore.showShippingFormErrors[key] = isCityValid(buyFlowStore.shippingAddress[key]);
      break;
    case "zipcode":
      buyFlowStore.showShippingFormErrors[key] = isZipValid(buyFlowStore.shippingAddress[key]);
      break;
  }
}

export function extractFirstLastFromFullName(fullName: string): string[] {
  const nameElements = fullName.split(" ");
  let firstName = "";
  let lastName = "";
  if (nameElements && nameElements?.length > 0) {
    firstName = nameElements[0];
    if (nameElements.length > 1) {
      lastName = nameElements.reduce((p, c, i) => {
        if (i == 1) return c;
        return `${p} ${c}`;
      });
    }
  }
  return [firstName, lastName];
}

export function setShippingAddressFromCC(paymentMethod: IPaymentMethod) {
  const buyFlowStore = useBuyFlowStore();
  const paymentMethodAddress = paymentMethod?.address;
  if (buyFlowStore.cart) {
    //this logic gets us a preliminary crack at first/last name
    //because we're putting it into the shipping address, it is possible for the user to edit this
    //we may want to save this adjusted first/last name to pass to Allstate
    //also, PayPal does give us a separated first/last name, so the payment method may include
    //already separated names that we can use directly in the future
    let name = ["", ""];
    if (paymentMethod?.accountHolder) {
      name = extractFirstLastFromFullName(paymentMethod.accountHolder);
    }
    buyFlowStore.cart.shippingAddress = {
      firstName: name[0],
      lastName: name[1],
      address1: paymentMethodAddress.streetAddress1 || "",
      address2: paymentMethodAddress.streetAddress2 || "",
      city: paymentMethodAddress.cityName || "",
      state: paymentMethodAddress.stateOrProvince || "",
      zipcode: paymentMethodAddress.postalCode || "",
      country: paymentMethodAddress.country || "",
    };
  }
}

export function setPaymentMethodById(paymentMethodId: string) {
  const paypalStore = usePayPalStore();
  const buyFlowStore = useBuyFlowStore();

  const paymentMethod = buyFlowStore.creditCards.find(l => l.id === paymentMethodId);
  if (paymentMethod) {
    buyFlowStore.paymentType =
      paymentMethod.cardType === paymentTypes.PayPal ? paymentTypes.PayPal : paymentTypes.CreditCard;

    if (paymentMethod.cardType === paymentTypes.PayPal) {
      const payPalResponse = {
        email: paymentMethod.email ?? "",
        paymentMethodId: paymentMethodId,
        shippingAddress: buyFlowStore.hasShippableItem
          ? ({
              streetAddress1: paymentMethod.address.streetAddress1,
              streetAddress2: paymentMethod.address.streetAddress2,
              cityName: paymentMethod.address.cityName,
              stateOrProvince: paymentMethod.address.stateOrProvince,
              postalCode: paymentMethod.address.postalCode,
              country: paymentMethod.address.country,
            } as IPayPalShippingAddressInfo)
          : null,
      } as IPayPalApprovalResponse;

      paypalStore.setPayPalBillingAgreementResponseManually(payPalResponse);
    }

    //Set the shipping info if the user has not input a different one
    if (buyFlowStore.shippingMatchesBilling && buyFlowStore.hasShippableItem) {
      setShippingAddressFromCC(paymentMethod);
      if (buyFlowStore.cart?.shippingAddress) {
        buyFlowStore.shippingAddress = buyFlowStore.cart.shippingAddress;
      }
    }
  }

  buyFlowStore.updateCreditCard(paymentMethodId);
}

export async function continueToMyAccount(forceDownLoadPage = false) {
  addBuyFlowLogEntry("Go to My Account clicked: continueToMyAccount - BuyHelper.ts");
  const buyFlowStore = useBuyFlowStore();
  const userStore = useUserStore();
  const siteSettingsStore = useSiteSettingsStore();

  let newRoute = buyFlowStore.cart?.brand === openTextBrands.Webroot ? "downloads" : "backup";
  if (buyFlowStore.isAllStateOrVpn()) {
    newRoute = "home";
  }

  if (forceDownLoadPage) {
    newRoute = "downloads";
  }

  if (userStore.currentUser) {
    buyFlowStore.showProcessingOrderMessage = true;
    //Skip the security questions for new accounts and logged in users.
    siteSettingsStore.setSkipAccountSetup(true);

    // Update stores before getting back to navigation -- Wait 5 secs for provision to complete
    await new Promise(resolve => setTimeout(resolve, 5000));

    await Promise.all([
      useSubscriptionsStore().forceRefreshAndPopulateSubscriptions(),
      useSecurityStore().forceRefresh(),
      getUserDetailsFromStore(true),
      reloadSubscriberInfo(),
    ]);
    buyFlowStore.showProcessingOrderMessage = false;

    if (buyFlowStore.isNewUser) {
      siteSettingsStore.redirectPreliminaryUserToBackup = true;
      //If a new user (preliminary account), VD shall send the customer to the password set up page
      router.push({ name: "setNewPassword" });
    } else {
      //If an existing user (established account) logged in, VD shall send the customer to the backup tab
      router.push(`/${newRoute}`);
    }
  } else {
    //If an existing user (established account) did not log in, VD shall send the customer to the log in page and then the backup tab
    // this gets the current domain and maintains it in the returnUrl sent to Login
    const currentDomain = new URL(window.location.href);
    const redirectDomain = currentDomain.origin + "/";
    if (userStore.email) {
      window.location.href = getRedirectToLoginURLWithEmail(`${redirectDomain}${newRoute}`, userStore.email);
    } else {
      window.location.href = getRedirectToLoginURL(`${redirectDomain}${newRoute}`);
    }
  }
}

export function getLicenseKeyFromCartItems(cartItems: ICartItem[]) {
  return getCartItemWithLicenseKey(cartItems)?.licenseKey ?? "";
}

export function getCartItemWithLicenseKey(cartItems: ICartItem[]) {
  return cartItems.find(l => l.licenseKey);
}

//This should only be used if the user is not logged in.
export function getWebrootInterstitialLinkUnauthenticated() {
  const buyFlowStore = useBuyFlowStore();
  const portalDestParam = generateInterstitialReturnPortalDestination("&");
  // SECURITY: tab-scoped storage so the license key does not persist after the tab closes
  const licenseKey = buyFlowStore.licenseKey || sessionStorage.getItem("licenseKey");
  return `${getUrl("WEBROOT_INTERSTITIAL")}${licenseKey}${portalDestParam}`;
}

//Does a case insensitive look up for the given item's value from the query params
export function getQueryParam(params: LocationQuery, item: string) {
  let paramValue = "";
  if (params !== null) {
    for (const [key, value] of Object.entries(params)) {
      if (key.toLowerCase() === item.toLowerCase()) {
        paramValue = value ? value.toString() : "";
        break;
      }
    }
  }

  return paramValue;
}

export async function addProduct(sku: string, mainItem: ICartItem | undefined) {
  const buyFlowStore = useBuyFlowStore();
  const product = buyFlowStore?.plans.find(l => l.sku === sku);
  let ratePlan: IRatePlan | undefined = undefined;

  //if we have a product to add then remove anything that's in the same upgrade group
  if (product) {
    ratePlan = bestMatchingCandyRackRatePlan(product, true);
    const sameProductGroupSkus = buyFlowStore?.plans
      .filter(p => p.upgradeGroup === product.upgradeGroup && p.upgradeGroup != null)
      .map(p => p.sku);
    if (buyFlowStore.cart && buyFlowStore.cart.items) {
      for (const [index, item] of buyFlowStore.cart.items.entries()) {
        if (sameProductGroupSkus.some(s => s === item.sku)) {
          await buyFlowStore.removeFromCart(item, index);
        }
      }
    }
  }
  //Clear the undo feature
  buyFlowStore.clearUndo();

  let quantity = mainItem?.quantity || 1;
  //Set the quantity
  if (product && quantity > 1 && buyFlowStore.getMaxQuantityBySku(product.sku)) {
    quantity =
      quantity > buyFlowStore.getMaxQuantityBySku(product.sku)
        ? buyFlowStore.getMaxQuantityBySku(product.sku)
        : quantity;
  }

  const item = {
    sku: product?.sku,
    ratePlanId: ratePlan?.ratePlanId,
    quantity: quantity,
    autoRenew: true,
    total: ratePlan?.newSubscriptionPrice,
  } as ICartItem;
  buyFlowStore.cart?.items.push(item);
}

export function getPlanetOpenTextDiscountStatus() {
  const buyFlowStore = useBuyFlowStore();
  const subscriptionStore = useSubscriptionsStore();

  if (buyFlowStore.cart?.enrollmentGuid && buyFlowStore.cart?.enrollmentGuid != EMPTY_GUID) {
    return true;
  } else if (buyFlowStore.cart && subscriptionStore.subscriptions) {
    return buyFlowStore.cart.items.some(item => {
      const hasPlanetOtSub = subscriptionStore.subscriptions.some(
        sub => sub.licenseKey === item.licenseKey && sub.isPlanetOt && sub.ratePlanId === item.ratePlanId
      );
      return hasPlanetOtSub;
    });
  } else {
    return false;
  }
}
